import { useEffect, useState } from "react";
import LogicFrame from "./LogicFrame";
import { TermText, useLogicTerms } from "./TermChip";
import { fetchAbilities, fetchRecords, createRecord, updateRecord, deleteRecord, fetchCourse } from "@/lib/logicApi";
import { useSearchParams, Link } from "react-router-dom";
import { CompletionPanel, ExerciseReturnBar } from "@/components/academy/LessonNav";
import { flatLessons, BTN_FX, DISCIPLINE_THEME } from "@/lib/academyTheme";
import { useLogicUi } from "./logicUi";

const ROJ_DRAFT_KEY = "stoicos.logic.record.draft";

const GROUPS = [
  { title: "The Event", fields: ["context", "impression", "facts", "interpretations", "firstReaction"] },
  { title: "The Division of Control", fields: ["withinControl", "outsideControl"] },
  { title: "The Examination", fields: ["examinedJudgment", "assentDecision", "relevantVirtue"] },
  { title: "The Action", fields: ["chosenAction", "expectedConsequence"] },
  { title: "Afterwards", fields: ["actualOutcome", "afterReflection", "recogniseEarlier"], later: true },
];
const VIRTUES = ["wisdom", "justice", "courage", "temperance"];
const ASSENT = ["assent", "withhold", "reject"];

function Field({ def, value, onChange, whyValue, onWhyChange }) {
  const { ui, L, opt } = useLogicUi();
  if (def.kind === "assent") {
    return (
      <div>
        <p className="text-sm text-bone/90 font-reading mb-1">{L(def.label)}</p>
        <p className="text-xs text-fog/60 font-reading mb-3">{L(def.hint)}</p>
        <div className="flex flex-wrap gap-2 mb-3">
          {ASSENT.map((v) => (
            <button key={v} type="button" data-testid={`record-assent-${v}`} onClick={() => onChange(v)} aria-pressed={value === v}
              className={`px-4 py-2 text-[11px] tracking-[0.16em] uppercase border ${value === v ? "border-gold text-gold bg-gold/10" : "border-white/15 text-fog/70 hover:border-gold/40"}`}>{opt(v)}</button>
          ))}
        </div>
        <textarea rows={2} value={whyValue || ""} onChange={(e) => onWhyChange(e.target.value)} data-testid="record-assent-why"
          placeholder={ui.whyPh}
          className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
      </div>
    );
  }
  if (def.kind === "virtue") {
    return (
      <div>
        <p className="text-sm text-bone/90 font-reading mb-1">{L(def.label)}</p>
        <p className="text-xs text-fog/60 font-reading mb-3">{L(def.hint)}</p>
        <div className="flex flex-wrap gap-2">
          {VIRTUES.map((v) => (
            <button key={v} type="button" data-testid={`record-virtue-${v}`} onClick={() => onChange(v)} aria-pressed={value === v}
              className={`px-4 py-2 text-[11px] tracking-[0.16em] uppercase border capitalize ${value === v ? "border-gold text-gold bg-gold/10" : "border-white/15 text-fog/70 hover:border-gold/40"}`}>{opt(v)}</button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div>
      <label className="block text-sm text-bone/90 font-reading mb-1">{L(def.label)}</label>
      <p className="text-xs text-fog/60 font-reading mb-2">{L(def.hint)}</p>
      <textarea rows={def.kind === "text_later" ? 2 : 3} value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={`record-field-${def.id}`}
        className="w-full bg-[#f3e9d2]/[0.05] border border-[#c8a95a]/25 text-bone font-reading p-3 text-sm focus:outline-none focus:ring-2 focus:ring-gold/50" />
    </div>
  );
}

function RecordForm({ fields, initial, onSaved, onCancel }) {
  const { ui } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [values, setValues] = useState(() => {
    if (initial) return initial;
    try { return JSON.parse(localStorage.getItem(ROJ_DRAFT_KEY)) || {}; } catch { return {}; }
  });
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const set = (id, v) => setValues((x) => {
    const nx = { ...x, [id]: v };
    if (!initial?.id) { try { localStorage.setItem(ROJ_DRAFT_KEY, JSON.stringify(nx)); } catch { /* full */ } }
    return nx;
  });
  const save = async () => {
    setSaving(true); setErr("");
    try {
      if (initial?.id) await updateRecord(initial.id, values);
      else { await createRecord(values); try { localStorage.removeItem(ROJ_DRAFT_KEY); } catch { /* ignore */ } }
      onSaved();
    } catch (e) { setErr(e.message || "Save failed"); }
    finally { setSaving(false); }
  };
  const byId = Object.fromEntries(fields.map((f) => [f.id, f]));
  return (
    <div className="border border-gold/30 bg-[#171c26] p-6 md:p-8 space-y-10" data-testid="record-form">
      {GROUPS.map((g) => (
        <section key={g.title}>
          <h3 className="text-xs tracking-[0.3em] uppercase text-gold/80 mb-2">{ui.recordGroups[g.title] || g.title}</h3>
          {g.later && <p className="text-xs text-fog/60 font-reading italic mb-4">{ui.laterNote}</p>}
          <div className="space-y-6 mt-4">
            {g.fields.map((id) => byId[id] && (
              <Field key={id} def={byId[id]} value={values[id]} onChange={(v) => set(id, v)}
                whyValue={values.assentWhy} onWhyChange={(v) => set("assentWhy", v)} />
            ))}
          </div>
        </section>
      ))}
      {err && <p className="text-sm text-red-300" data-testid="record-form-error">{err}</p>}
      <div className="flex gap-4">
        <button data-testid="record-form-save" onClick={save} disabled={saving}
          className="px-8 py-3 bg-gold/90 text-ink text-[11px] tracking-[0.22em] uppercase hover:bg-gold disabled:opacity-40">
          {saving ? "Saving" : initial?.id ? "Save changes" : "Enter into the Record"}
        </button>
        <button onClick={onCancel} className="px-6 py-3 border border-white/15 text-fog/70 text-[11px] tracking-[0.22em] uppercase hover:border-gold/40">Cancel</button>
      </div>
    </div>
  );
}

function EntryCard({ rec, fields, onEdit, onDelete }) {
  const { ui, L } = useLogicUi();
  const [open, setOpen] = useState(false);
  const complete = rec.actualOutcome && rec.afterReflection;
  return (
    <div className="border border-[#c8a95a]/20 bg-[#f3e9d2]/[0.04]" data-testid={`record-entry-${rec.id}`}>
      <button onClick={() => setOpen(!open)} className="w-full text-left px-6 py-4 flex items-center justify-between gap-4 focus:outline-none focus:ring-2 focus:ring-gold/60" aria-expanded={open}>
        <span>
          <span className="text-[10px] tracking-[0.22em] uppercase text-[#8fa3c8]">{(rec.createdAt || "").slice(0, 10)}</span>
          <span className="block font-serif text-bone mt-1">{rec.context || rec.impression || ui.untitled}</span>
        </span>
        <span className={`text-[10px] tracking-[0.18em] uppercase whitespace-nowrap px-2 py-1 border ${complete ? "border-gold/60 text-gold" : "border-[#8fa3c8]/50 text-[#b9c6de]"}`}>
          {complete ? ui.complete : ui.outcomePending}
        </span>
      </button>
      {open && (
        <div className="px-6 pb-6 space-y-3">
          {fields.map((f) => {
            const v = f.id === "assentDecision" ? [rec.assentDecision, rec.assentWhy].filter(Boolean).join(": ") : rec[f.id];
            return v ? (
              <p key={f.id} className="text-sm font-reading leading-relaxed">
                <span className="text-gold/70">{L(f.label)} · </span>
                <span className="text-fog/90 capitalize-first">{v}</span>
              </p>
            ) : null;
          })}
          <div className="flex gap-4 pt-3 border-t border-white/10">
            <button data-testid={`record-edit-${rec.id}`} onClick={onEdit} className="text-[10px] tracking-[0.2em] uppercase text-gold hover:underline">
              {complete ? ui.edit : ui.completeEntry}
            </button>
            <button data-testid={`record-delete-${rec.id}`} onClick={onDelete} className="text-[10px] tracking-[0.2em] uppercase text-fog/50 hover:text-red-300">{ui.remove}</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecordOfJudgment() {
  const { ui, L, t } = useLogicUi();
  const th = DISCIPLINE_THEME.logic;
  const [fields, setFields] = useState([]);
  const [records, setRecords] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | record
  const [justSaved, setJustSaved] = useState(false);
  const [returnCtx, setReturnCtx] = useState(null);
  const [searchParams] = useSearchParams();
  const terms = useLogicTerms();

  const load = () => fetchRecords().then((d) => setRecords(d.records || []));
  useEffect(() => { fetchAbilities().then((d) => setFields(d.recordFields || [])); load(); }, []);
  useEffect(() => {
    const fromLesson = searchParams.get("fromLesson");
    try {
      const draft = JSON.parse(localStorage.getItem(ROJ_DRAFT_KEY) || "null");
      if (fromLesson || (draft && Object.values(draft).some((v) => String(v || "").trim()))) setEditing({});
    } catch { if (fromLesson) setEditing({}); }
    if (!fromLesson) return;
    fetchCourse().then((course) => {
      const all = flatLessons(course);
      const idx = all.findIndex((l) => l.slug === fromLesson);
      if (idx < 0) return;
      setReturnCtx({ lesson: all[idx], prev: all[idx - 1] || null, next: all[idx + 1] || null, phaseSlug: all[idx].phaseSlug });
    }).catch(() => {});
  }, [searchParams]);

  const draftGuard = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(ROJ_DRAFT_KEY) || "null");
      return !!draft && Object.values(draft).some((v) => String(v || "").trim());
    } catch { return false; }
  };

  return (
    <LogicFrame title="Record of Judgment" testid="logic-journal-page" draftGuard={draftGuard}>
      <ExerciseReturnBar discipline="logic" returnCtx={returnCtx} locTitle={L} />
      {justSaved && <div className="mb-10"><CompletionPanel discipline="logic" returnCtx={returnCtx} locTitle={L} /></div>}
      <div className="max-w-2xl mb-12 space-y-4">
        <p className="text-fog/90 font-reading leading-[1.9]">{ui.recordIntro1}</p>
        <p className="text-fog/70 font-reading text-sm leading-[1.85]">
          <TermText terms={terms} text={ui.recordIntro2} />
        </p>
        <p className="text-[11px] tracking-[0.18em] uppercase">
          <Link to="/academy/my-work" className="text-[#b9c6de] hover:text-gold" data-testid="record-mywork-link">{t("academy.nav.myWork")} →</Link>
        </p>
      </div>

      {editing !== null ? (
        <RecordForm fields={fields} initial={editing.id ? editing : null}
          onSaved={() => { setEditing(null); setJustSaved(true); load(); }} onCancel={() => setEditing(null)} />
      ) : (
        <button data-testid="record-new-entry" onClick={() => setEditing({})}
          className={`px-8 py-4 text-[11px] font-semibold tracking-[0.22em] uppercase ${th.btn} ${BTN_FX} ${th.ring}`}>
          {ui.beginNew}
        </button>
      )}

      <div className="mt-14 space-y-4">
        {records.length === 0 && editing === null && (
          <p className="text-fog/50 font-reading italic" data-testid="logic-journal-empty">
            {ui.recordEmpty}
          </p>
        )}
        {records.map((r) => (
          <EntryCard key={r.id} rec={r} fields={fields}
            onEdit={() => setEditing(r)}
            onDelete={async () => { await deleteRecord(r.id); load(); }} />
        ))}
      </div>
    </LogicFrame>
  );
}
